// Explicit .ts extension: unit-tested by Node directly.
import { HOME_REGION, type MarineRegion } from '../marineRegions.ts';

/**
 * Which marine regions to offer as tabs, and in what order.
 *
 * Forty-two regions will not fit in a strip, and a fixed strip would either
 * privilege one coast or bury the rest. So the strip follows the map: the
 * regions whose water is on screen are the ones offered, which means panning to
 * Florida turns them into Miami and Key West without the reader hunting.
 *
 * San Francisco is always first and always present, on screen or not. It is
 * what this site is about, and a tab that moves — or disappears when you pan
 * away — is a tab you cannot rely on.
 */

export interface Viewport {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * A ceiling on the strip, high enough never to bite in practice.
 *
 * There is deliberately no small cap. Counted against real viewports, the water
 * on screen belongs to one region over the Bay, two locally, five across a
 * region and seven along a coast — a limit of six was cutting the strip exactly
 * where it was becoming useful, and along the Gulf, where nineteen offices
 * share a coastline, it hid most of them.
 *
 * This exists only so that zooming out to the whole planet, where thirty-eight
 * regions are technically in frame, does not build a wall of tabs. The strip
 * scrolls past a few rows, so reaching this is untidy rather than broken.
 */
export const MAX_TABS = 40;

/**
 * Longitude overlap, allowing for a box that crosses the antimeridian.
 *
 * Three Aleutian zones do. Such a box is stored with west greater than east and
 * means two spans — west to 180, and -180 to east — so a plain `w <= east &&
 * e >= west` reports no overlap exactly where the zone is.
 */
function lonOverlaps(w: number, e: number, west: number, east: number): boolean {
  if (w <= e) return w <= east && e >= west;
  return (w <= east && west <= 180) || (e >= west && east >= -180);
}

function overlaps(box: readonly [number, number, number, number], v: Viewport): boolean {
  const [w, s, e, n] = box;
  if (s > v.north || n < v.south) return false;
  return lonOverlaps(w, e, v.west, v.east);
}

/**
 * How far past the edge of the map a region still counts as being in view.
 *
 * A fifth of what is on screen. Strict overlap read correctly but felt broken:
 * looking down the California coast at zoom 7, Eureka's nearest zone begins at
 * 40.0 North and the top of the map was 39.8, so it was missing by a fifth of a
 * degree — and a nudge of the zoom flicked it in and out of the strip.
 *
 * A region whose water is just off the edge is one you are about to pan to, so
 * offering it early costs nothing and makes the strip hold still.
 */
export const VIEW_MARGIN = 0.2;

/** The view, grown by the margin on every side. */
export function padViewport(v: Viewport, fraction = VIEW_MARGIN): Viewport {
  const dx = (v.east - v.west) * fraction;
  const dy = (v.north - v.south) * fraction;
  return {
    west: v.west - dx,
    east: v.east + dx,
    // Latitude cannot usefully run past the poles.
    south: Math.max(-90, v.south - dy),
    north: Math.min(90, v.north + dy),
  };
}

/** How much of the view a region covers, as a count of its zones in frame. */
function zonesInView(region: MarineRegion, v: Viewport): number {
  return region.zones.reduce((n, [, box]) => n + (overlaps(box, v) ? 1 : 0), 0);
}

export interface TabPlan {
  /** Regions to render as tabs, home first. */
  tabs: MarineRegion[];
  /** Everything else, for the "More" list. */
  rest: MarineRegion[];
}

/**
 * @param regions  Every region, in any order.
 * @param view     The current map bounds.
 * @param selected The region currently showing, which must always be a tab —
 *                 otherwise choosing one from "More" would make it vanish.
 */
export function planTabs(
  regions: readonly MarineRegion[],
  view: Viewport,
  selected: string = HOME_REGION,
  maxTabs: number = MAX_TABS,
): TabPlan {
  const home = regions.find((r) => r.id === HOME_REGION);

  // Judged against a slightly larger view than the map shows, so a region just
  // off the edge is offered rather than flickering as the zoom changes.
  const padded = padViewport(view);

  // By how much of each region is in frame, so the one you are looking at
  // outranks one clipping the corner of the view.
  const nearby = regions
    .filter((r) => r.id !== HOME_REGION)
    .map((r) => ({ r, n: zonesInView(r, padded) }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n || a.r.name.localeCompare(b.r.name))
    .map((x) => x.r);

  let tabs = [...(home ? [home] : []), ...nearby].slice(0, maxTabs);

  /*
   * The selection is forced in only if it would not appear anyway, and then at
   * the end.
   *
   * Hoisting it to the front instead meant clicking a tab moved it out from
   * under the pointer — you then had to hunt for the region you had just
   * chosen in order to hover it. A tab that is already on screen now keeps its
   * exact position when chosen, and one picked from "More" arrives at the end
   * without shifting anything already there.
   */
  if (selected !== HOME_REGION && !tabs.some((r) => r.id === selected)) {
    const chosen = regions.find((r) => r.id === selected);
    if (chosen) tabs = [...tabs.slice(0, Math.max(0, maxTabs - 1)), chosen];
  }

  const shown = new Set(tabs.map((r) => r.id));

  return {
    tabs,
    rest: regions
      .filter((r) => !shown.has(r.id))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}

/** Every zone box of a region, for framing it on the map. */
export function regionBoxes(
  region: MarineRegion,
): Array<[number, number, number, number]> {
  return region.zones.map(([, box]) => box);
}

/** Every zone id of a region, for outlining it under the pointer. */
export function regionZoneIds(region: MarineRegion): string[] {
  return region.zones.map(([id]) => id);
}
